import React, { useEffect, useState } from "react";
import { Form } from "react-bootstrap";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import Label from "../Label/Label";
import CustomButton from "../CustomButton/CustomButton";
import ModalPopup from "../ModalPopup/ModalPopup";
import {
  assertAdminAuth,
  formatDisplayDate,
  getApiErrorMessage,
} from "../../services/apiClient";
import { isAdminUser, isLoggedIn } from "../../util/auth";
import {
  blockMeetingSlot,
  cancelMeetingBooking,
  fetchMeetingBookings,
  fetchMeetingInviteEmails,
  formatMeetingDateLabel,
  formatSlotTimeLabel,
  getBrowserTimezone,
  updateMeetingInviteEmails,
} from "../../services/meetingApi";
import "./MeetingAdminTab.scss";

const EMPTY_EMAILS = ["", "", "", "", ""];

const toIsoDate = (value) => {
  if (!value) {
    return "";
  }

  const parsedDate = value instanceof Date ? value : new Date(value);

  if (Number.isNaN(parsedDate.getTime())) {
    return "";
  }

  return parsedDate.toISOString().slice(0, 10);
};

const canManageMeetings = () => isLoggedIn() && isAdminUser();

const MeetingAdminTab = ({ labels = {} }) => {
  const [bookings, setBookings] = useState([]);
  const [inviteEmails, setInviteEmails] = useState(EMPTY_EMAILS);
  const [inviteUpdatedAt, setInviteUpdatedAt] = useState("");
  const [loadError, setLoadError] = useState("");
  const [inviteError, setInviteError] = useState("");
  const [isLoading, setIsLoading] = useState(true);
  const [isSavingEmails, setIsSavingEmails] = useState(false);
  const [showBlockModal, setShowBlockModal] = useState(false);
  const [blockForm, setBlockForm] = useState({
    date: null,
    startTime: "10:00",
    endTime: "10:30",
    reason: "",
  });
  const [isBlocking, setIsBlocking] = useState(false);

  const loadBookings = async () => {
    if (!canManageMeetings()) {
      setIsLoading(false);
      setLoadError("Admin login required to manage meetings.");
      return;
    }

    setIsLoading(true);
    setLoadError("");

    try {
      const items = await fetchMeetingBookings();
      setBookings(items);
    } catch (error) {
      setBookings([]);
      setLoadError(getApiErrorMessage(error, "Failed to load meeting bookings."));
    } finally {
      setIsLoading(false);
    }
  };

  const loadInviteEmails = async () => {
    if (!canManageMeetings()) {
      return;
    }

    setInviteError("");

    try {
      const data = await fetchMeetingInviteEmails();
      const emails = [...(data?.emails || [])];

      while (emails.length < 5) {
        emails.push("");
      }

      setInviteEmails(emails.slice(0, 5));
      setInviteUpdatedAt(data?.updatedAt || "");
    } catch (error) {
      setInviteError(
        getApiErrorMessage(error, "Failed to load meeting invite emails."),
      );
    }
  };

  useEffect(() => {
    loadBookings();
    loadInviteEmails();
  }, []);

  const handleSaveInviteEmails = async () => {
    if (!assertAdminAuth()) {
      return;
    }

    const emails = inviteEmails.map((email) => email.trim()).filter(Boolean);

    if (!emails.length) {
      window.alert("Add at least one invite email.");
      return;
    }

    if (emails.length > 5) {
      window.alert("You can configure up to 5 invite emails.");
      return;
    }

    setIsSavingEmails(true);

    try {
      const data = await updateMeetingInviteEmails(emails);
      const savedEmails = [...(data?.emails || emails)];

      while (savedEmails.length < 5) {
        savedEmails.push("");
      }

      setInviteEmails(savedEmails.slice(0, 5));
      setInviteUpdatedAt(data?.updatedAt || "");
      window.alert("Meeting invite emails updated.");
    } catch (error) {
      window.alert(getApiErrorMessage(error, "Failed to update invite emails."));
    } finally {
      setIsSavingEmails(false);
    }
  };

  const handleCancelBooking = async (contactId) => {
    if (!assertAdminAuth()) {
      return;
    }

    if (!window.confirm("Cancel this meeting booking and free the slot?")) {
      return;
    }

    try {
      await cancelMeetingBooking(contactId);
      await loadBookings();
    } catch (error) {
      window.alert(getApiErrorMessage(error, "Failed to cancel booking."));
    }
  };

  const handleBlockSlot = async () => {
    if (!assertAdminAuth()) {
      return;
    }

    if (!blockForm.date) {
      window.alert("Please select a date.");
      return;
    }

    setIsBlocking(true);

    try {
      await blockMeetingSlot({
        date: toIsoDate(blockForm.date),
        startTime: blockForm.startTime,
        endTime: blockForm.endTime,
        timezone: getBrowserTimezone(),
        reason: blockForm.reason || undefined,
      });

      setShowBlockModal(false);
      setBlockForm({
        date: null,
        startTime: "10:00",
        endTime: "10:30",
        reason: "",
      });
      window.alert("Slot blocked successfully.");
    } catch (error) {
      window.alert(getApiErrorMessage(error, "Failed to block slot."));
    } finally {
      setIsBlocking(false);
    }
  };

  return (
    <div className="meeting_Admin_Tab">
      <div className="meeting_Admin_Header">
        <Label title={labels.title || "Meeting"} className="heading" />
        <CustomButton
          title={labels.blockBtn || "Block slot"}
          onClick={() => setShowBlockModal(true)}
        />
      </div>

      {loadError && <Label title={loadError} className="meeting_Load_Error" />}

      <section className="meeting_Invite_Section">
        <Label
          title={labels.inviteTitle || "Meeting invite emails (up to 5)"}
          className="section_Title"
        />
        <p className="meeting_Invite_Help">
          These addresses receive meeting confirmations and cancellations.
        </p>
        <div className="meeting_Invite_Form">
          {inviteEmails.map((email, index) => (
            <Form.Control
              key={`invite-email-${index}`}
              type="email"
              value={email}
              placeholder={`Admin email ${index + 1}`}
              onChange={(event) => {
                const nextEmails = [...inviteEmails];
                nextEmails[index] = event.target.value;
                setInviteEmails(nextEmails);
              }}
            />
          ))}
        </div>
        {inviteUpdatedAt && (
          <p className="meeting_Invite_Updated">
            Last updated: {formatDisplayDate(inviteUpdatedAt)}
          </p>
        )}
        {inviteError && <p className="meeting_Invite_Error">{inviteError}</p>}
        <CustomButton
          title={isSavingEmails ? "Saving..." : labels.saveInviteBtn || "Save invite emails"}
          onClick={handleSaveInviteEmails}
        />
      </section>

      <section className="meeting_Bookings_Section">
        <Label title={labels.bookingsTitle || "Meeting bookings"} className="section_Title" />

        <div className="meetingContainer">
          <div className="tab">
            <div>SL. NO</div>
            <div>Name</div>
            <div>Email</div>
            <div>Service</div>
            <div>Meeting</div>
            <div>Status</div>
            <div>Action</div>
          </div>

          <div className="tableData">
            {isLoading ? (
              <div className="customers empty_Row">
                <div>Loading bookings...</div>
              </div>
            ) : bookings.length === 0 ? (
              <div className="customers empty_Row">
                <div>No meeting bookings found.</div>
              </div>
            ) : (
              bookings.map((booking, index) => (
                <div className="customers" key={booking.contactId || booking.bookingId}>
                  <div>{index + 1}.</div>
                  <div>{booking.name}</div>
                  <div>{booking.email}</div>
                  <div>{booking.serviceType}</div>
                  <div className="meeting_Slot_Cell">
                    {formatMeetingDateLabel(booking.meetingDate)}
                    <br />
                    {formatSlotTimeLabel(booking.meetingStartTime)} –{" "}
                    {formatSlotTimeLabel(booking.meetingEndTime)}
                    {booking.meetingLink && (
                      <>
                        <br />
                        <a
                          href={booking.meetingLink}
                          target="_blank"
                          rel="noreferrer"
                        >
                          Join link
                        </a>
                      </>
                    )}
                  </div>
                  <div>{booking.meetingStatus || "—"}</div>
                  <div className="meeting_Actions">
                    {booking.meetingStatus !== "CANCELLED" && (
                      <span
                        className="icon-delete cursorP"
                        title={labels.cancelBtn || "Cancel booking"}
                        onClick={() => handleCancelBooking(booking.contactId)}
                      />
                    )}
                  </div>
                </div>
              ))
            )}
          </div>
        </div>
      </section>

      <ModalPopup
        showModal={showBlockModal}
        onHide={() => setShowBlockModal(false)}
        onHideClick={() => setShowBlockModal(false)}
        onButtonClick={handleBlockSlot}
        headerTitle={labels.blockModalTitle || "Block meeting slot"}
        buttonTitle={isBlocking ? "Blocking..." : "Block slot"}
      >
        <Form>
          <Form.Group className="mb-3">
            <Form.Label>Date</Form.Label>
            <DatePicker
              selected={blockForm.date}
              onChange={(date) =>
                setBlockForm((current) => ({ ...current, date }))
              }
              className="form-control"
              placeholderText="Select date"
              dateFormat="dd-MM-yyyy"
              minDate={new Date()}
            />
          </Form.Group>
          <Form.Group className="mb-3">
            <Form.Label>Start time (HH:mm)</Form.Label>
            <Form.Control
              value={blockForm.startTime}
              onChange={(event) =>
                setBlockForm((current) => ({
                  ...current,
                  startTime: event.target.value,
                }))
              }
              placeholder="10:00"
            />
          </Form.Group>
          <Form.Group className="mb-3">
            <Form.Label>End time (HH:mm)</Form.Label>
            <Form.Control
              value={blockForm.endTime}
              onChange={(event) =>
                setBlockForm((current) => ({
                  ...current,
                  endTime: event.target.value,
                }))
              }
              placeholder="10:30"
            />
          </Form.Group>
          <Form.Group className="mb-3">
            <Form.Label>Reason (optional)</Form.Label>
            <Form.Control
              as="textarea"
              rows={2}
              value={blockForm.reason}
              onChange={(event) =>
                setBlockForm((current) => ({
                  ...current,
                  reason: event.target.value,
                }))
              }
            />
          </Form.Group>
        </Form>
      </ModalPopup>
    </div>
  );
};

export default MeetingAdminTab;
